import { detectDateCadence } from './detectDateCadence.ts';
import {
	type StatusUpdateCadence,
	detectStatusUpdateCadence,
} from './detectStatusUpdateCadence.ts';
import { matchesAnyKeyword } from './matchesAnyKeyword.ts';
import { parseTitleDate } from './parseTitleDate.ts';
import { STATUS_UPDATE_WORD_KEYWORDS } from './statusUpdateSignalKeywords.ts';
import type { StatusUpdateSignalInput } from './getStatusUpdateSignalMatch.ts';

/**
 * Cadence used when no signal on the page (title, children or parent) resolves a
 * more specific rhythm. Weekly is the most common status-update cadence, so it is
 * the safest default for the Rovo prompt.
 */
export const DEFAULT_STATUS_UPDATE_CADENCE: StatusUpdateCadence = 'weekly';

/**
 * Detects the cadence of a status-update series from its children by looking at
 * the rhythm of their dates. Only children whose title carries an update word
 * ("update", "status", "recap", "report") are considered, mirroring
 * `getStatusUpdateSignalMatch`. Title dates are preferred over creation dates
 * because an author-controlled title date ("Update 17/06") is a stronger cadence
 * signal than when the page happened to be created.
 */
const detectChildrenCadence = (
	children: StatusUpdateSignalInput['children'],
): StatusUpdateCadence | undefined => {
	if (!children || children.length === 0) {
		return undefined;
	}

	const statusUpdateKeywords = STATUS_UPDATE_WORD_KEYWORDS;
	const relevantChildren = children.filter((child) =>
		matchesAnyKeyword(child.title, statusUpdateKeywords),
	);
	if (relevantChildren.length === 0) {
		return undefined;
	}

	const titleDates: number[] = [];
	const creationDates: (string | number | Date)[] = [];
	for (const child of relevantChildren) {
		creationDates.push(child.createdAt);
		const titleDate = parseTitleDate(child.title);
		if (titleDate !== undefined) {
			titleDates.push(titleDate);
		}
	}

	return detectDateCadence(titleDates) ?? detectDateCadence(creationDates);
};

/**
 * Resolves the cadence to use for a status update, combining every available
 * signal and always returning a concrete cadence so callers (e.g. the Rovo
 * status-update prompt) never have to special-case an unknown cadence.
 *
 * Precedence, from most to least specific:
 *   1. The page's own title ("Weekly status update" -> weekly).
 *   2. The observed rhythm of the children series (a folder of dated updates).
 *   3. The parent / folder title ("Monthly recaps" -> monthly).
 *   4. {@link DEFAULT_STATUS_UPDATE_CADENCE} (weekly) when nothing else matches.
 */
export const resolveStatusUpdateCadence = (input: StatusUpdateSignalInput): StatusUpdateCadence => {
	const { title, parentTitle, children } = input;

	const titleCadence = title ? detectStatusUpdateCadence(title) : undefined;
	if (titleCadence) {
		return titleCadence;
	}

	const childrenCadence = detectChildrenCadence(children);
	if (childrenCadence) {
		return childrenCadence;
	}

	const parentCadence = parentTitle ? detectStatusUpdateCadence(parentTitle) : undefined;
	if (parentCadence) {
		return parentCadence;
	}

	return DEFAULT_STATUS_UPDATE_CADENCE;
};
